import React from "react";
import { Button, Col, Container, Modal, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { isNullOrUndefined } from "util";
import { talkToSales } from "../../../docuclipper/api";
import {
  StripePlan,
  StripeSubscription,
} from "../../../docuclipper/DocuclipperTypes";
import { logErrorToGA } from "../../../docuclipper/Logger";
import { ReduxState } from "../../../redux";
import { createAlert, CreateAlertFn } from "../../../redux/reducers/Alerts";
import { closeModal, openModal } from "../../../redux/reducers/ModalManager";
import { SwitchPlans } from "../../Pages/Account/ChangePlan/SwitchPlans";
import { SelectPlanAfterTrial } from "../../Pages/Account/SelectPlanAfterTrial/SelectPlanAfterTrial";
import { getNextUpgradeOption } from "../../Pages/Account/SelectPlanAfterTrial/tierUtils";
import { WithAccountAndPlans } from "../../Pages/Account/utils";
import TrackedButton from "../TrackedButton";

type Props = {
  open: boolean;
  data: { [k: string]: any };
  closeCreditsModal: () => any;
  plans: StripePlan[];
  subscription: StripeSubscription;
  createAlert: CreateAlertFn;
};

export const SubscriptionModalUnconnected: React.FC<
  Props & RouteComponentProps
> = ({
  open,
  data,
  closeCreditsModal,
  createAlert,
  plans,
  subscription,
  history,
}) => {
  const dispatch = useDispatch();
  const toggle = () => closeCreditsModal();

  const onKeyPress = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      toggle();
    }
  };

  const neededCredits = data?.neededCredits;
  const totalCredits = data?.totalCredits;
  const includedPages = subscription?.docuclipperPlan?.numPages ?? 0;
  const multiplier =
    includedPages > 0 && neededCredits
      ? Math.max(1, Math.round(Number(neededCredits) / includedPages))
      : null;

  const nextUpgrade =
    data?.outOfCredits &&
    plans?.length > 0 &&
    subscription?.status === "active" &&
    includedPages > 0
      ? getNextUpgradeOption(
          plans,
          includedPages,
          (subscription.docuclipperPlan.billingCycle as any) || "Monthly"
        )
      : null;

  const trialAlert =
    subscription && subscription.status === "trialing"
      ? ` Your trial is limited to 20 pages.`
      : "";

  return (
    <>
      <Modal
        size="xl"
        onHide={toggle}
        keyboard={true}
        onKeyPress={onKeyPress}
        show={open}
      >
        <Modal.Header closeButton={true}>
          <div className="page-title-icon">
            <i className="pe-7s-piggy" />
            <span className="ml-2">
              {subscription?.status === "trialing"
                ? "Choose a plan to unlock full access"
                : data?.outOfCredits && subscription?.status === "active"
                ? "Monthly page limit reached. Choose how to continue."
                : subscription?.status === "canceled"
                ? "Reactivate your account"
                : "Choose how to continue"}
            </span>
          </div>
        </Modal.Header>
        <Modal.Body>
          {data?.outOfCredits && (
            <p className="text-muted mb-3">
              {multiplier != null && multiplier > 1
                ? `You've processed ${multiplier}× your plan's included pages.`
                : "You've used all the pages included in your plan this month."}
              {" "}Let's choose the best option for your continued processing.
              {!isNullOrUndefined(neededCredits) && !isNullOrUndefined(totalCredits) && (
                <span className="ml-1">
                  ({neededCredits?.toLocaleString()} needed, {totalCredits?.toLocaleString()} available
                  {trialAlert})
                </span>
              )}
            </p>
          )}

          {data?.outOfCredits && subscription?.status === "active" && (
            <div className="mb-4">
              <Row>
                <Col md={4} className="mb-3">
                  <div className="p-3 border border-success rounded h-100">
                    <strong className="text-success">1. Upgrade plan</strong>
                    <p className="small text-muted mb-2 mt-1">
                      Lower your cost per page. Best for most users.
                    </p>
                    {nextUpgrade ? (
                      <TrackedButton
                        action="outOfPagesUpgradeNextTierClick"
                        variant="success"
                        size="sm"
                        className="w-100"
                        onClick={() => {
                          closeCreditsModal();
                          history.push("/account/changePlans");
                        }}
                      >
                        Upgrade to {nextUpgrade.displayName} · {nextUpgrade.priceDisplay}
                      </TrackedButton>
                    ) : (
                      <TrackedButton
                        action="outOfPagesViewPlansClick"
                        variant="success"
                        size="sm"
                        className="w-100"
                        onClick={() => {
                          closeCreditsModal();
                          history.push("/account/changePlans");
                        }}
                      >
                        View plan options
                      </TrackedButton>
                    )}
                  </div>
                </Col>
                <Col md={4} className="mb-3">
                  <div className="p-3 rounded h-100" style={{ border: "2px solid #856404" }}>
                    <strong style={{ color: "#533f03" }}>2. Add page pack</strong>
                    <p className="small text-muted mb-2 mt-1">
                      One-off spikes. No commitment. Pages never expire.
                    </p>
                        <Button
                          variant="outline-dark"
                          size="sm"
                          className="w-100"
                          style={{ borderColor: "#856404", color: "#533f03" }}
                          onClick={() => {
                            closeCreditsModal();
                            dispatch(openModal("buyPages", { outOfPages: true }));
                          }}
                        >
                          Buy pages
                        </Button>
                  </div>
                </Col>
                <Col md={4} className="mb-3">
                  <div className="p-3 border border-primary rounded h-100">
                    <strong className="text-primary">3. Extended limit</strong>
                    <p className="small text-muted mb-2 mt-1">
                      Pay-as-you-go at your plan's per-page rate. Billed at end of month.
                    </p>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="w-100"
                      onClick={() => {
                        closeCreditsModal();
                        history.push("/account?section=plan#overage");
                      }}
                    >
                      Continue at plan rate with extended limit
                    </Button>
                  </div>
                </Col>
              </Row>
              <p className="text-muted small mb-0">Or choose another plan below.</p>
            </div>
          )}

          {subscription && subscription.status === "trialing" && (
            <div className="my-4">
              <SelectPlanAfterTrial
                redirectToAccountOnConfirmed={false}
                onConfirmed={() => closeCreditsModal()}
                plans={plans}
                subscription={subscription}
              />
            </div>
          )}
          {subscription && subscription.status === "canceled" && (
            <div className="my-4">
              <SwitchPlans
                redirectToAccountOnConfirmed={false}
                onConfirmed={() => closeCreditsModal()}
                plans={plans}
                subscription={subscription}
                createAlert={createAlert}
              />
            </div>
          )}
          {subscription &&
            subscription.status !== "past_due" &&
            subscription.status !== "trialing" &&
            subscription.status !== "canceled" && (() => {
              const pn = subscription.docuclipperPlan.name as string;
              const isEnterpriseTier = pn === "Enterprise" || /^Enterprise\s/.test(pn);
              return !isEnterpriseTier;
            })() && (
              <div className="my-4">
                <SwitchPlans
                  redirectToAccountOnConfirmed={false}
                  onConfirmed={() => closeCreditsModal()}
                  plans={plans}
                  subscription={subscription}
                  createAlert={createAlert}
                />
              </div>
            )}
          {subscription && subscription.status === "past_due" && (
            <>
              <Container style={{ marginLeft: "auto", marginRight: "auto" }}>
                <div className="my-2">
                  Select a plan to continue using DocuClipper
                </div>
                <TrackedButton
                  action="outOfPagesSelectPlanClick"
                  className="m-2"
                  variant="primary"
                  onClick={() => {
                    closeCreditsModal();
                    history.push("/account/upgrade");
                  }}
                >
                  Select Plan
                </TrackedButton>
              </Container>
            </>
          )}
          {subscription &&
            subscription.status !== "trialing" &&
            subscription.status === "active" && (() => {
              const pn = subscription.docuclipperPlan.name as string;
              return pn === "Enterprise" || /^Enterprise\s/.test(pn);
            })() && (
              <Container style={{ marginLeft: "auto", marginRight: "auto" }}>
                <div className="text-center" style={{ maxWidth: "600px" }}>
                  <div className="my-2">{`Your plan includes ${subscription.docuclipperPlan.numPages} pages/month. Need more pages? Contact support to switch to a custom plan that fits your needs.`}</div>

                  <Row>
                    <Col>
                      <TrackedButton
                        action="contactSupportClick"
                        className="m-2"
                        variant="secondary"
                        onClick={() => {
                          try {
                            talkToSales();
                            closeCreditsModal();
                            createAlert({
                              message: `Request received. We'll be in touch soon`,
                              timeout: 0,
                              type: "success",
                            });
                          } catch (err) {
                            closeCreditsModal();
                            logErrorToGA(err);
                            createAlert({
                              message: `Error sending request`,
                              timeout: 0,
                              type: "error",
                            });
                          }
                        }}
                      >
                        Contact support
                      </TrackedButton>
                      {/* <Button
                        variant="primary"
                        className="m-2"
                        onClick={() => history.push("/account?section=plan")}
                      >
                        Buy Pages
                      </Button> */}
                    </Col>
                  </Row>
                </div>
              </Container>
            )}
          {subscription &&
            subscription.status === ("Local" as any) &&
            subscription.seat &&
            subscription.admin &&
            !subscription.seat.stripePriceId && (
              <Container style={{ marginLeft: "auto", marginRight: "auto" }}>
                <div className="text-center">
                  <div className="my-2">{`The account is out of pages. We've notified the account administrator (${subscription.admin.email}).`}</div>
                  <div className="m-2">
                    Need to finish right now? Create your own account
                  </div>
                  <div>
                    <Button
                      variant="primary"
                      className="m-2"
                      onClick={() => history.push("/signup")}
                    >
                      Create Account
                    </Button>
                  </div>
                </div>
              </Container>
            )}
          {subscription &&
            subscription.status === ("Local" as any) &&
            subscription.seat &&
            subscription.admin &&
            subscription.seat.stripePriceId && (
              <Container style={{ marginLeft: "auto", marginRight: "auto" }}>
                <div className="my-2">{`The account is out of pages. We've notified the account administrator (${subscription.admin.email}).`}</div>
              </Container>
            )}
        </Modal.Body>
      </Modal>
    </>
  );
};

const SubscriptionModalWithData = withRouter(
  WithAccountAndPlans(withRouter(SubscriptionModalUnconnected))
) as any;

export const SubscriptionModal: React.FC = () => {
  const dispatch = useDispatch();

  const { activeModal, modals } = useSelector(
    (state: ReduxState) => state.ModalManager
  );
  if (activeModal !== "outOfPages") {
    return null;
  }
  const data = modals.filter((x) => x.name === "outOfPages");
  return (
    <SubscriptionModalWithData
      open={activeModal === "outOfPages"}
      data={data.length > 0 ? data[0].data : null}
      closeCreditsModal={() => dispatch(closeModal("outOfPages"))}
      createAlert={(x) => dispatch(createAlert(x))}
    />
  );
};
